/**
 * The page budget dashboard — `GET /v1/admin/seo/budget` (docs/05-admin/23 §3.5, backend 40 §5).
 *
 * Read-only. Every number is the server's: the meter's addends are the counted registry and the
 * `seo.static_asset_estimate` setting, the last-build panel is the exporter's own manifest, and
 * the composition is one GROUP BY over the registry, so its rows sum to the tier totals above it.
 *
 * ⛔ **Nothing here is estimated in the client.** The dashboard exists to be the place the three
 * budget checks agree — promotion refuses past the budget, the exporter counts real files and fails
 * the build, the nightly drift check compares the registry with what shipped — and a figure worked
 * out in the browser would be a fourth opinion that answers to none of them.
 *
 * Every count drills into the registry screen already filtered to it.
 */
import { useNavigate } from '@tanstack/react-router';
import { Card, Kicker, StatusChip } from '../components/primitives';
import { ErrorState, LoadingState } from '../components/feedback';
import { DataTable } from '../components/data';
import { StatTile } from '../components/kit';
import { errorMessage } from '../lib/api/client';
import { forModuleJump } from '../app/urlState';
import {
  drillSearch,
  useSeoBudget,
  type SeoBudget as Budget,
  type SeoComposition,
  type SeoRegistryFilter,
} from '../lib/api/hooks/seo';
import { TIERS, TIER_MEANS } from './SeoRegistry';
import { REBALANCED, describeFlip } from './seoFlips';
import { formatDateTime } from '../lib/datetime';

/** The categorical ramp of doc 03 §6.9, one step per tier. */
export const TIER_FILL = {
  prerendered: 'var(--c-saffron)',
  edge: 'var(--c-gold)',
  excluded: 'var(--c-ink3)',
} as const;

/** The headline sum, written out addend by addend so it can be checked by eye. */
export function meterSentence(b: Budget): string {
  const used = b.prerendered + b.static_asset_estimate;
  return `${b.prerendered} prerendered pages + ~${b.static_asset_estimate} other files (assets, sitemap chunks, worker, redirects) = ${used} of ${b.budget_max} · headroom ${b.headroom}`;
}

/** Wall-clock minutes and seconds between two instants, for the last build's duration. */
export function duration(startedAt: string, finishedAt: string): string {
  const seconds = Math.max(0, Math.round((Date.parse(finishedAt) - Date.parse(startedAt)) / 1000));
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

export function rowTotal(row: SeoComposition): number {
  return row.prerendered + row.edge + row.excluded;
}

/**
 * Backend 40 §9's flapping alert, in words. The server decides `flapping`; this only says what
 * the two runs moved against the threshold it applied, so the number on screen is the server's.
 */
export function churnSentence(f: Budget['flapping']): string {
  if (f.runs.length === 0) return 'No nightly promotion run has finished yet.';
  const moved = f.runs.map((r) => r.churn).join(' and ');
  const runs = f.runs.length === 1 ? 'The last nightly run' : 'The last two nightly runs';
  return f.flapping
    ? `${runs} moved ${moved} pages, each over the alert line of ${f.threshold} — pages are swinging across the waterline. Check seo.demotion_margin.`
    : `${runs} moved ${moved} pages. The alert fires when two runs in a row each move more than ${f.threshold}.`;
}

/** The three budget checks, one line each — the order they run in. */
export const TRIPLE_ASSERTION: readonly string[] = [
  'The nightly promotion job never promotes past the budget; a manual promote is refused the same way.',
  'The exporter counts the real files it wrote and fails the build if they exceed the budget.',
  'The nightly drift check compares what the registry expected with what the last build shipped.',
];

export function SeoBudget() {
  const budget = useSeoBudget();
  const navigate = useNavigate();
  const drill = (seoFilter: SeoRegistryFilter) =>
    navigate({
      to: '/content/seo/registry',
      search: (prev: Record<string, unknown>) => ({ ...forModuleJump(prev), ...drillSearch(seoFilter) }),
    } as never);

  if (budget.isPending) return <LoadingState message="Reading the page budget…" />;
  if (budget.isError) return <ErrorState title="Could not read the page budget" cause={errorMessage(budget.error)} />;

  const b = budget.data;
  const used = b.prerendered + b.static_asset_estimate;
  const share = Math.min(100, Math.round((used / Math.max(1, b.budget_max)) * 100));
  const over = b.headroom < 0;
  const build = b.last_build;
  const failed = build ? build.over_budget || build.mismatched : false;

  return (
    <>
      <Card variant="flat" padding="var(--sp-lg)">
        <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
          <Kicker>Deploy files against the budget</Kicker>
          <div
            role="meter"
            aria-label="Deploy files used"
            aria-valuemin={0}
            aria-valuemax={b.budget_max}
            aria-valuenow={used}
            style={{ height: 12, background: 'var(--c-sunk)', borderRadius: 'var(--r-tile)', overflow: 'hidden' }}
          >
            <div style={{ width: `${share}%`, height: '100%', background: over ? 'var(--c-error)' : 'var(--c-saffron)' }} />
          </div>
          <span style={{ fontSize: 'var(--fs-admin-ui)', color: over ? 'var(--c-error-text)' : 'var(--c-ink2)' }}>
            {meterSentence(b)}
            {over && ' — over budget: the next nightly run sheds unpinned pages'}
          </span>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 'var(--sp-md)' }}>
        <StatTile label="Prerendered" value={b.prerendered} note={`${b.prerendered_locked} pinned`} onClick={() => drill({ tab: 'prerendered' })} />
        <StatTile label="Pinned" value={b.prerendered_locked} note="prerendered and held by hand" onClick={() => drill({ tab: 'pinned' })} />
        <StatTile label="Edge" value={b.edge} note="rendered on first request" onClick={() => drill({ tab: 'edge' })} />
        <StatTile label="Excluded" value={b.excluded} note="not rendered, not in the sitemap" onClick={() => drill({ tab: 'excluded' })} />
      </div>

      <Card variant="flat" padding="var(--sp-lg)">
        <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
          <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'center' }}>
            <Kicker>Last build</Kicker>
            {build && <StatusChip tone={failed ? 'error' : 'success'} label={failed ? 'Failed a check' : 'Passed'} />}
          </div>
          {build ? (
            <span style={{ fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink2)' }}>
              {formatDateTime(build.finished_at)} · {duration(build.started_at, build.finished_at)} ·{' '}
              {build.file_count} files shipped against {build.expected_files} expected ({build.page_count} pages,{' '}
              {build.asset_count} assets, {build.skipped_count} skipped)
              {build.over_budget && ' · over budget'}
              {build.mismatched && ` · drift of ${build.drift} files against the registry`}
            </span>
          ) : (
            <span style={{ fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink3)' }}>
              No build has reported a manifest yet. The meter above is the registry&rsquo;s count, not a deploy&rsquo;s.
            </span>
          )}
          <ul style={{ margin: 0, paddingLeft: 'var(--sp-lg)', fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
            {TRIPLE_ASSERTION.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      </Card>

      <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
        <Kicker>Tier composition by page type</Kicker>
        {b.composition.length === 0 ? (
          <span style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink3)' }}>
            The registry is empty — the nightly scoring job has not filled it yet.
          </span>
        ) : (
          <>
            {/* The chart. Its exact values are the table below it (doc 03 §6.9). */}
            <div aria-hidden="true" style={{ display: 'grid', gap: 6 }}>
              {b.composition.map((row) => (
                <div key={row.page_type} style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 'var(--sp-sm)', alignItems: 'center' }}>
                  <span style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>{row.page_type}</span>
                  <div style={{ display: 'flex', height: 10, borderRadius: 'var(--r-tile)', overflow: 'hidden', background: 'var(--c-sunk)' }}>
                    {TIERS.map((t) => (
                      <div key={t} title={`${t}: ${row[t]}`} style={{ flex: row[t], background: TIER_FILL[t] }} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <DataTable
              density="compact"
              onRowClick={(row) => drill({ pageType: String(row.id) })}
              columns={[
                { key: 'type', label: 'Page type', mono: true },
                ...TIERS.map((t) => ({ key: t, label: t, align: 'right' as const })),
                { key: 'total', label: 'Total', align: 'right' },
              ]}
              rows={b.composition.map((row) => ({
                id: row.page_type,
                type: row.page_type,
                prerendered: row.prerendered,
                edge: row.edge,
                excluded: row.excluded,
                total: rowTotal(row),
              }))}
              footer={<span>{TIERS.map((t) => `${t}: ${TIER_MEANS[t]}`).join(' · ')}</span>}
            />
          </>
        )}
      </div>

      <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
        <Kicker>Flip history</Kicker>
        <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'center', fontSize: 'var(--fs-admin-cell)' }}>
          {b.flapping.flapping && <StatusChip tone="error" label="Flapping" />}
          <span style={{ color: b.flapping.flapping ? undefined : 'var(--c-ink3)' }}>{churnSentence(b.flapping)}</span>
        </div>
        {b.flips.length === 0 ? (
          <span style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink3)' }}>
            No tier has changed yet, by hand or by the nightly run.
          </span>
        ) : (
          <DataTable
            density="compact"
            columns={[
              { key: 'when', label: 'When' },
              { key: 'kind', label: 'Kind' },
              { key: 'who', label: 'By' },
              { key: 'what', label: 'Change' },
              { key: 'why', label: 'Reason' },
            ]}
            rows={b.flips.map((f) => ({
              id: f.id,
              when: formatDateTime(f.created_at),
              kind: f.action === REBALANCED ? 'nightly run' : `page #${f.entity_id}`,
              who: f.actor_admin_id ? `admin #${f.actor_admin_id}` : f.actor_type,
              what: describeFlip(f),
              why: f.reason || '—',
            }))}
          />
        )}
      </div>
    </>
  );
}
